import { useCallback, useEffect, useState } from 'react';
import { Modal, Grid, Button, Box } from '@mui/material';
import { generateRadioButtonConfigs } from '../../constants/prize-constants';
import { RadioButton } from '../inputComponents/RadioButton';
import TextFieldWrapper from '../../common/TextFieldWrapper/TextFieldWrapper';
import TooltipText from '../inputComponents/TooltipText';
import { ToolTipPlacement } from '../../types/componentTypes/tooltip';
import {  getIn } from 'formik';

const PrizeImageMetadataModal = ({ props }) => {

    const {
        showModal,
        language,
        url,
        formik,
        setModalState,
    } = props;

    const [radioButtonConfigs, setRadioButtonConfigs] = useState([]);
    const [currentUrlIndex, setCurrentUrlIndex] = useState<number>(0);

    const handleDiscardAndCloseModal = () => {
        const updatedData = { ...formik.values.images_metadata };
        if (Object.keys(updatedData).length) {
            delete updatedData[language][currentUrlIndex];
            if (Object.keys(updatedData[language]).length === 0) {
                delete updatedData[language];
            }
            if (Object.keys(updatedData).length === 0) {
                formik.setFieldValue('images_metadata', undefined);
            } else {
                formik.setFieldValue('images_metadata', updatedData);
            }
        }

        setModalState({open: false, index: null, url: '' });
    };

    const handleSaveModal = () => {
        ['priority', 'activeStatus'].forEach(key => {
            const metadataVal = formik.values.images_metadata[language]?.at(currentUrlIndex)?.[key];
            if (metadataVal) {
                formik.setFieldValue(
                    `images_metadata[${language}][${currentUrlIndex}][${key}]`,
                    key === 'priority' ? parseInt(metadataVal) : JSON.parse(metadataVal)
                );
            }
        })
        setModalState({open: false, index: null, url: '' });
        setCurrentUrlIndex(0);
    };

    const handleCloseModal = () => {
        setModalState({open: false, index: null, url: '' });
        setCurrentUrlIndex(0);
    }

    const execCurrIndexAndHandleMetadataNotExist = useCallback( async () => {
        return new Promise((resolve) => {
            if(showModal) {
                const metadata = getIn(formik.values, 'images_metadata')
                if((!metadata || !(language in metadata))) {
                    formik.setFieldValue(`images_metadata[${language}]`, [])
                }
                const images = getIn(formik.values, `tempParams.img_url[${language}]`)
                if(images?.includes(url) && !metadata?.[language]?.find(obj => obj?.url === url)) {
                    const existingArr = metadata?.[language] || []
                    formik.setFieldValue(`images_metadata[${language}]`,[...existingArr, { url }])
                }
            }
            resolve('')
        })
    }, [formik, showModal, language, url])

    useEffect(() => {
        const handleImageMetadataModal = async () => {
            await execCurrIndexAndHandleMetadataNotExist()
                .then(() => {
                    if(showModal) {
                        const currUrlIndex = getIn(formik.values, `images_metadata[${language}]`)?.findIndex(obj => obj?.url === url)
                        setCurrentUrlIndex(currUrlIndex)
                    }
                })
        }
        handleImageMetadataModal()
    }, [formik.values, language, url, showModal, execCurrIndexAndHandleMetadataNotExist])
    useEffect(() => {
        setRadioButtonConfigs(generateRadioButtonConfigs(language, currentUrlIndex));
    }, [language, currentUrlIndex]);

    return (
        <Modal
            open={showModal}
            onClose={handleCloseModal}
        >
            <Box className='modal_main'>
                <Grid container justifyContent='center' spacing={2} direction={'column'}>
                    {radioButtonConfigs.map((config, index) => (
                        <Grid item key={index}>
                            <RadioButton
                                formLabel={config.formLabel}
                                row={config.row || false}
                                formik={formik}
                                values={config.values}
                                radioGroupName={config.radioGroupName}
                            />
                        </Grid>
                    ))}
                    <Grid item key='img-alt-text'>
                        <TooltipText
                            text={'Prize image alt text'}
                            tooltip={'The purpose of the Prize Image alternative text field is to provide a brief description of the image. If the image doesn’t load, the alt will be displayed instead.'}
                            textVariant='body1'
                            iconSize='medium'
                            styleName='prize-tooltip-text'
                            placement={ToolTipPlacement.RightEnd}
                        />
                        <TextFieldWrapper
                            name={`imgAltText_${language}_${currentUrlIndex}`}
                            value={formik.values?.images_metadata?.[language]?.at(currentUrlIndex)?.alt}
                            multiline
                            sx={{ width: "350px"}}
                            inputProps={{ maxLength: 1000 }}
                            helperText={'The maximum length is 1000 characters'}
                            onChange={(event) => {
                                formik.setFieldValue(`images_metadata.[${language}][${currentUrlIndex}].alt`, event.target.value)
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <Button  variant="outlined" onClick={handleDiscardAndCloseModal}>
                            Discard
                        </Button>
                        <Button variant="contained" onClick={handleSaveModal}>
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
}

export default PrizeImageMetadataModal;
